import {
    ChangeDetectorRef,
    Component,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    DAT,
    DATType,
    DailyActivityTrackerDetail,
} from 'app/modules/daily-activity-tracker/model';
import { DailyTrackerService } from 'app/modules/daily-activity-tracker/services/daily-tracker.service';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import module from 'app/modules/daily-activity-tracker/daily-activity-tracker.module';
import { Router } from '@angular/router';
import { FormComponent } from 'app/modules/daily-activity-tracker/components/form/form.component';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { toast } from 'ngx-sonner';
import { EMPTY, map, merge, Subject, switchMap, take, takeUntil } from 'rxjs';
import { composeUrl } from 'app/core/shared/helper/url.helper';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { DatSearchStateService } from '../../state/search.state';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';

@Component({
    selector: 'app-daily-activity-tracker',
    templateUrl: './index.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./index.page.scss'],
})
export class IndexPage {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any;
    startingIndex: number = 0;
    isLoading: boolean = false;

    sortInfo: string;
    private columnLabels: { [key: string]: string } = {
        ...DEFAULT_COLUMN_LABELS,
        plannedDate: 'Planned Date',
        startDate: 'Start Date',
        endDate: 'End Date',
        activityType: 'Activity Type',
        dailyActivity: 'Daily Activity',
        activityStatus: 'Status',
        statusUpdatedAt: 'Status Updated At',
    };

    columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
        key,
        name,
        isChecked: false, // default unchecked
      }));

    constructor(
        private service: DailyTrackerService,
        private dialog: MatDialog,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        private state: DatSearchStateService,
    ) { }

    ngOnInit() {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort);
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page)
                .pipe(
                    switchMap(() => {
                        // this.isLoading = true;

                        let pageInfo = {
                            count: this._paginator.length,
                            pageSize: this._paginator.pageSize,
                            limit: this._paginator.pageSize,
                            offset: this._paginator.pageIndex,
                        };
                        this.setPage(pageInfo); // Emit page number

                        return EMPTY;
                    }),
                    map(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe();
        }
    }

    onSort(sort: any) {
        if (!sort) return;
        this.searchForm.patchValue({
            OrderBy1: sort.active,
            OrderDir: sort.direction,
        });

        this.sortInfo = this.columnLabels[sort.active] || sort.active;
        this.setPage({ offset: 0 });
    }

    handleSearch(event$) {
        const field = event$?.Field;
        const platform = event$?.Platform;
        const well = event$?.Well;
        const string = event$?.String;

        if ([field, platform, well, string].some(v => v != null && v !== '')) {
            this.setPage({ offset: 0 });
        }
    }

    setSearch() {
        this.sortInfo = 'Updated Date';
        this.searchForm = this.formBuilder.group({
            Page: [0],
            PageSize: [20],
            OrderBy1: [null],
            OrderBy2: [null],
            OrderBy3: [null],
            OrderDir: 'asc',
            WellName: [''],
            PlatformName: [''],
            StringName: [''],
            FieldName: [''],
        });

        this.searchForm
            .get('PageSize')
            .valueChanges.subscribe((value) => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0;
        this.setPage({ offset: this.dt.page.offset });
    }

    downloadExcel() {
        const url = composeUrl(
            `/v1/DailyActivityTracker/DownloadOverallDailyActivityExcel`
        );
        this.service.downloadExcel(url);
    }

    setPage(pageInfo) {
        const saved = this.state.get()
        let params: any = {}

        // if have saved search
        if (saved) {
            // is search for changes
            const useSaved = !this.searchForm.dirty;

            this.searchForm.patchValue(
                useSaved ? saved : { Page: pageInfo.offset + 1 },
                { emitEvent: !useSaved }
            );

            this.dt.page.offset = pageInfo.offset;
            this.startingIndex = this.dt.page.offset * (useSaved ? saved.PageSize : this.dt.page.size);
            params = useSaved ? saved : this.searchForm.value;

        } else {
            this.dt.page.offset = pageInfo.offset;
            this.searchForm.patchValue({ Page: this.dt.page.offset + 1 });
            this.startingIndex = this.dt.page.offset * this.dt.page.size;
            params = this.searchForm.value;
        }

        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName,
        };

        this.service
            .getDailyActivityTracker({ ...params, search })
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                this.dt.page.totalRecords = pagedData.data.totalRecords;
                this.dt.rows = pagedData.data.data;
            });
    }

    reorderByDate(details: DailyActivityTrackerDetail[]) {
        details.sort(function compare(a, b) {
            const dateA: any = new Date(a.activityDate);
            const dateB: any = new Date(b.activityDate);
            return dateA - dateB;
        });
        return details;
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
        this.state.clear(); // clear session storage
    }

    handleFormatDate(dateString: string, withTime: boolean = true) {
        return handleFormatDate(dateString, withTime);
    }

    update(row: DAT) {
        const dialogRef = this.dialog.open(FormComponent, {
            data: {
                payload: row,
                type: row.activityType,
            },
            width: '70%',
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((isUpdate) => {
                if (isUpdate) {
                    this.setPage({ offset: this.dt.page.offset });
                }
            });
    }

    toActivity(dat: DAT) {
        let path = '';
        let params = {};

        if (this.searchForm.dirty) this.state.set(this.searchForm.value);

        switch (dat.activityType) {
            case DATType.pm:
                path = `/data-management/cdft/${dat.activityId}/edit`;
                params = {};
                break;
            case DATType.apr:
                path = `/data-management/annulus-pressure-record/${dat.activityId}/edit`;
                params = {};
                break;
            case DATType.cm:
                path = `/data-management/corrective-maintenance/${dat.activityId}/edit`;
                params = {};
                break;
            default:
                toast.error('Activity Type Not Recognized');
                return;
        }

        this.router.navigate([path], params);
    }

    isActivity(dat: DAT) {
        const activities: any = [DATType.pm, DATType.apr, DATType.cm];
        return !activities.includes(dat.activityType as string);
    }

    applySort(payload: ApplySortPayload) {
        // Patch form (fill missing with null)
        const [OrderBy1, OrderBy2, OrderBy3] = [
          payload.columns[0]?.key ?? null,
          payload.columns[1]?.key ?? null,
          payload.columns[2]?.key ?? null,
        ];
    
        this.searchForm.patchValue({
          OrderBy1,
          OrderBy2,
          OrderBy3,
          OrderDir: payload.orderDir ?? 'asc',
        });
        
        const labels = payload.columns.map(k => k.name);
        this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
        this.setPage({ offset: 0 });
      }
}
